import { useState, type ComponentType } from 'react'
import { Ban, CalendarClock, PhoneMissed, PhoneOff, ThumbsDown, ThumbsUp, Unplug } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ROUTES } from '@/constants/routes'
import { AppModal } from '@/components/common/AppModal'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form-field'
import { Textarea } from '@/components/ui/textarea'
import { CallbackPicker } from '@/modules/calls/components/CallbackPicker'
import { useCallConfig, useCallDetail, useCancelCall, useWrapUpCall } from '@/modules/calls/hooks/useCalls'
import type { CallOutcome } from '@/modules/calls/services/call.service'
import { useCallStore } from '@/modules/calls/store/call.store'
import { wallTimeToIso, type WallTime } from '@/modules/calls/utils/callback-time'
import { getApiErrorToastMessage } from '@/utils/form-errors'
import { cn } from '@/utils/cn'

type CallbackRule = 'required' | 'optional' | 'retry' | 'none'

/** Mirrors the API's OUTCOME_RULES (pms-backend src/modules/calls/callRules.js). */
const OUTCOMES: {
  value: CallOutcome
  label: string
  icon: ComponentType<{ className?: string }>
  noteRequired: boolean
  callback: CallbackRule
  tone: string
}[] = [
  {
    value: 'interested',
    label: 'Interested',
    icon: ThumbsUp,
    noteRequired: true,
    callback: 'optional',
    tone: 'text-emerald-600 dark:text-emerald-400',
  },
  {
    value: 'follow_up',
    label: 'Follow-up',
    icon: CalendarClock,
    noteRequired: true,
    callback: 'required',
    tone: 'text-primary',
  },
  {
    value: 'no_answer',
    label: 'No answer',
    icon: PhoneMissed,
    noteRequired: false,
    callback: 'retry',
    tone: 'text-amber-600 dark:text-amber-400',
  },
  {
    value: 'busy',
    label: 'Busy',
    icon: PhoneOff,
    noteRequired: false,
    callback: 'retry',
    tone: 'text-amber-600 dark:text-amber-400',
  },
  {
    value: 'not_interested',
    label: 'Not interested',
    icon: ThumbsDown,
    noteRequired: true,
    callback: 'none',
    tone: 'text-rose-600 dark:text-rose-400',
  },
  {
    value: 'wrong_number',
    label: 'Wrong number',
    icon: Ban,
    noteRequired: false,
    callback: 'none',
    tone: 'text-rose-600 dark:text-rose-400',
  },
  {
    value: 'dropped',
    label: 'Dropped',
    icon: Unplug,
    noteRequired: false,
    callback: 'retry',
    tone: 'text-muted-foreground',
  },
]

const CALLBACK_LABEL: Record<CallbackRule, string> = {
  required: 'Callback',
  optional: 'Callback',
  retry: 'Try again',
  none: '',
}

/** Result form for one call — opens after the call, also from the banner. */
export function CallOutcomeSheet() {
  const callId = useCallStore((s) => s.outcomeCallId)
  const close = useCallStore((s) => s.closeOutcome)
  const { data: config } = useCallConfig()
  const pendingCall = config?.pendingWrapUps.find((c) => c.id === callId) ?? null
  const { data: fetchedCall } = useCallDetail(callId, Boolean(callId) && !pendingCall)
  const call = pendingCall ?? fetchedCall ?? null

  return (
    <AppModal
      open={Boolean(callId)}
      onOpenChange={(open) => (open ? undefined : close())}
      title="Call result"
      description={call?.lead ? `${call.lead.businessName} · ${call.lead.leadNo}` : undefined}
      moduleId="sales"
      size="sm"
    >
      {/* Keyed per call so every call starts with an empty form. */}
      {callId ? <CallOutcomeForm key={callId} callId={callId} canCancel={call?.status === 'initiated'} /> : null}
    </AppModal>
  )
}

function CallOutcomeForm({ callId, canCancel }: { callId: string; canCancel: boolean }) {
  const close = useCallStore((s) => s.closeOutcome)
  const { data: config } = useCallConfig()
  const wrapUp = useWrapUpCall()
  const cancel = useCancelCall()

  const [outcome, setOutcome] = useState<CallOutcome | null>(null)
  const [note, setNote] = useState('')
  const [callback, setCallback] = useState<Partial<WallTime> | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [savingNext, setSavingNext] = useState(false)
  const navigate = useNavigate()

  const rule = OUTCOMES.find((o) => o.value === outcome) ?? null
  const callbackIso = callback ? wallTimeToIso(callback) : null
  const noteMissing = Boolean(rule?.noteRequired) && !note.trim()
  const callbackMissing = rule?.callback === 'required' && !callbackIso
  const callbackPartial = Boolean(callback) && !callbackIso
  const canSave = Boolean(rule) && !noteMissing && !callbackMissing && !callbackPartial
  const busy = wrapUp.isPending || cancel.isPending

  const pickOutcome = (value: CallOutcome) => {
    setOutcome(value)
    const next = OUTCOMES.find((o) => o.value === value)
    if (next?.callback === 'none') setCallback(null)
  }

  const onSave = async (next: boolean) => {
    setSubmitted(true)
    if (!rule || !canSave) return
    setSavingNext(next)
    try {
      const result = await wrapUp.mutateAsync({
        id: callId,
        input: {
          outcome: rule.value,
          note: note.trim() || undefined,
          callbackAt: callbackIso,
          next,
        },
      })
      close()
      if (!next) {
        toast.success('Result saved')
      } else if (result.next?.lead) {
        toast.success(`Next: ${result.next.lead.businessName}`)
        navigate(`${ROUTES.SALES_LEADS_VIEW.replace(':id', result.next.lead.id)}?call=next`)
      } else {
        toast.success(
          result.next?.reason === 'outside_hours' ? 'Saved. Calling hours are over' : 'Saved. No more leads to call',
        )
      }
    } catch (err) {
      toast.error(getApiErrorToastMessage(err, 'Could not save result'))
    }
  }

  const onDidNotCall = async () => {
    try {
      await cancel.mutateAsync(callId)
      close()
    } catch (err) {
      toast.error(getApiErrorToastMessage(err, 'Could not cancel call'))
    }
  }

  const hours = config?.settings ?? {
    callingStartMinute: 540,
    callingEndMinute: 1200,
    callingDays: [1, 2, 3, 4, 5, 6],
  }

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label="Call result" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {OUTCOMES.map((o) => {
          const Icon = o.icon
          const active = outcome === o.value
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => pickOutcome(o.value)}
              className={cn(
                'flex h-11 items-center gap-2 rounded-lg border px-3 text-left text-sm font-medium transition-colors',
                active ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'border-border hover:bg-muted',
              )}
            >
              <Icon className={cn('h-4 w-4 shrink-0', o.tone)} />
              <span className="truncate">{o.label}</span>
            </button>
          )
        })}
      </div>
      {submitted && !rule ? <p className="text-xs text-destructive">Pick a result</p> : null}

      {rule && rule.callback !== 'none' ? (
        <FormField
          label={CALLBACK_LABEL[rule.callback]}
          required={rule.callback === 'required'}
          error={submitted && (callbackMissing || callbackPartial) ? 'Pick date and time' : undefined}
        >
          <CallbackPicker
            value={callback}
            onChange={setCallback}
            hours={hours}
            required={rule.callback === 'required'}
          />
        </FormField>
      ) : null}

      {rule ? (
        <FormField
          label="Remark"
          htmlFor="callNote"
          required={rule.noteRequired}
          error={submitted && noteMissing ? 'Add a remark' : undefined}
        >
          <Textarea
            id="callNote"
            rows={3}
            maxLength={2000}
            className="min-h-0 resize-y text-sm"
            placeholder="Saved with Remarks"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormField>
      ) : null}

      <div className="flex flex-col-reverse gap-2 border-t border-border/60 pt-3 sm:flex-row sm:justify-between">
        {canCancel ? (
          <Button type="button" variant="ghost" className="h-9" onClick={onDidNotCall} disabled={busy}>
            Didn't call
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" className="h-9" onClick={close} disabled={busy}>
            Later
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-9"
            onClick={() => onSave(false)}
            loading={wrapUp.isPending && !savingNext}
            disabled={busy}
          >
            Save
          </Button>
          <Button
            type="button"
            className="h-9"
            onClick={() => onSave(true)}
            loading={wrapUp.isPending && savingNext}
            disabled={busy}
          >
            Save &amp; Next
          </Button>
        </div>
      </div>
    </div>
  )
}
